import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { usePrivacy } from '@/contexts/PrivacyContext';
import { useCurrencyTransition } from '@/contexts/CurrencyTransitionContext';
import { formatCompactNumber } from '@/utils/formatCompactNumber';

interface MoneyDisplayProps {
    /** The numeric value to display (in decimal form, e.g., 100.50) */
    value: number | null | undefined;
    /** Override the currency (defaults to user's displayCurrency) */
    currency?: string;
    /** Additional CSS classes */
    className?: string;
    /** Show +/- sign for positive/negative values */
    showSign?: boolean;
    /** Apply green/red color based on positive/negative value */
    colorize?: boolean;
    /** Text size variant */
    size?: 'sm' | 'md' | 'lg';
    /** Display large numbers (10M+) in compact format (e.g., 12.35 Mil). Defaults to true. */
    compact?: boolean;
    /** Force loading shimmer state (e.g., during initial data load) */
    isLoading?: boolean;
    /** Currency precision override for exact original-currency amounts. */
    fractionDigits?: number;
}

// Common currency symbols
const CURRENCY_SYMBOLS: Record<string, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    CNY: '¥',
    CHF: 'Fr',
    AUD: 'A$',
    CAD: 'C$',
    HKD: 'HK$',
    SGD: 'S$',
    SEK: 'kr',
    NOK: 'kr',
    DKK: 'kr',
    NZD: 'NZ$',
    MXN: 'Mex$',
    INR: '₹',
    BRL: 'R$',
    KRW: '₩',
    ZAR: 'R',
    RUB: '₽',
    TRY: '₺',
    PLN: 'zł',
    THB: '฿',
    IDR: 'Rp',
    MYR: 'RM',
    PHP: '₱',
    CZK: 'Kč',
    ILS: '₪',
    CLP: 'CLP$',
    TWD: 'NT$',
    ARS: 'AR$',
    COP: 'COL$',
    SAR: '﷼',
    AED: 'د.إ',
    RON: 'lei',
    HUF: 'Ft',
    BGN: 'лв',
    HRK: 'kn',
    ISK: 'kr',
    SDR: 'SDR',
    MDL: 'L',
};

// Commodity currencies that should always show at least 2 decimal places
const COMMODITY_CURRENCIES = ['XAU', 'XAG'];

/**
 * MoneyDisplay component - displays currency values using user settings for formatting.
 * Supports privacy blur mode via PrivacyContext.
 */
export function MoneyDisplay({
    value,
    currency,
    className = '',
    showSign = false,
    colorize = false,
    size = 'md',
    compact = true,
    isLoading = false,
    fractionDigits,
}: MoneyDisplayProps) {
    const { settings } = useAuth();
    const { isPrivacyModeEnabled } = usePrivacy();
    const { isCurrencyTransitioning } = useCurrencyTransition();

    const formattedValue = useMemo(() => {
        if (value === null || value === undefined) {
            return '-';
        }

        const displayCurrency = currency || settings?.displayCurrency || 'USD';
        const currencyDisplay = settings?.currencyDisplay || 'symbol';
        const currencyPosition = settings?.currencyPosition || 'before';

        // Commodity currencies (XAU, XAG) always show at least 2 decimals due to high value per unit
        const isCommodity = COMMODITY_CURRENCIES.includes(displayCurrency);
        const minFractionDigits = isCommodity
            ? Math.max(settings?.minimumFractionDigits ?? 2, 2)
            : (settings?.minimumFractionDigits ?? 2);
        // The user's setting is the ceiling. When the caller tells us the currency's real
        // precision, cap there too — showing more digits than the currency has is noise.
        const settingsMaxFractionDigits = isCommodity
            ? Math.max(settings?.maximumFractionDigits ?? 2, 2)
            : (settings?.maximumFractionDigits ?? 2);
        const maxFractionDigits = fractionDigits === undefined
            ? settingsMaxFractionDigits
            : Math.min(settingsMaxFractionDigits, fractionDigits);
        const thousandsSeparator = settings?.thousandsSeparator || ',';
        const decimalSeparator = settings?.decimalSeparator || '.';
        const useGrouping = settings?.useGrouping ?? true;

        // Get currency symbol or code
        const currencySymbol = currencyDisplay === 'symbol'
            ? (CURRENCY_SYMBOLS[displayCurrency] || displayCurrency)
            : displayCurrency;

        // Add sign if needed
        const sign = showSign && value > 0 ? '+' : (value < 0 ? '-' : '');

        // Check if compact formatting should be applied
        const compactResult = compact ? formatCompactNumber(value) : null;

        if (compactResult) {
            // Format compact number with 2 decimal places
            const compactNum = Math.abs(compactResult.number).toFixed(2);
            const formattedCompact = `${compactNum} ${compactResult.suffix}`;

            // Build final string based on position
            if (currencyPosition === 'before') {
                return `${sign}${currencySymbol}${formattedCompact}`;
            } else {
                return `${sign}${formattedCompact} ${currencySymbol}`;
            }
        }

        // Standard formatting for non-compact values
        const absValue = Math.abs(value);
        const parts = absValue.toFixed(maxFractionDigits).split('.');
        let integerPart = parts[0] ?? '0';
        let decimalPart = parts[1] || '';

        // Apply grouping (thousands separator)
        if (useGrouping) {
            integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
        }

        // Trim trailing zeros from decimal part, but keep at least minFractionDigits
        if (decimalPart) {
            // First, ensure we don't go below minFractionDigits
            while (decimalPart.length < minFractionDigits) {
                decimalPart += '0';
            }
            // Then trim trailing zeros only if we're above minFractionDigits
            while (decimalPart.length > minFractionDigits && decimalPart.endsWith('0')) {
                decimalPart = decimalPart.slice(0, -1);
            }
        }

        // Build formatted number
        let formattedNumber = integerPart;
        if (decimalPart) {
            formattedNumber += decimalSeparator + decimalPart;
        }

        // Build final string based on position
        if (currencyPosition === 'before') {
            return `${sign}${currencySymbol}${formattedNumber}`;
        } else {
            return `${sign}${formattedNumber} ${currencySymbol}`;
        }
    }, [value, currency, settings, showSign, compact, fractionDigits]);

    const sizeClasses = {
        sm: 'text-sm',
        md: 'text-base',
        lg: 'text-lg font-semibold',
    };

    const colorClasses = colorize && value !== null && value !== undefined
        ? value > 0
            ? 'text-green-700 dark:text-green-300'
            : value < 0
                ? 'text-red-700 dark:text-red-300'
                : ''
        : '';

    // Privacy mode: Keep blur effect to hide values
    if (isPrivacyModeEnabled) {
        return (
            <span
                className={`${sizeClasses[size]} ${className} blur-xs select-none whitespace-nowrap relative z-10`}
                aria-hidden="true"
            >
                {formattedValue}
            </span>
        );
    }

    // Currency transition or explicit loading: Show shimmer skeleton placeholder
    if (isCurrencyTransitioning || isLoading) {
        const charWidth = size === 'sm' ? 0.5 : size === 'lg' ? 0.7 : 0.6;
        const estimatedWidth = `${Math.max(formattedValue.length * charWidth, 3)}rem`;

        return (
            <span
                className={`${sizeClasses[size]} ${className} inline-block whitespace-nowrap relative z-10`}
                aria-busy="true"
                aria-label="Loading currency value"
            >
                <span
                    className="inline-block rounded-sm bg-linear-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 animate-shimmer bg-size-[200%_100%]"
                    style={{ width: estimatedWidth, height: '1em' }}
                />
            </span>
        );
    }

    return (
        <span className={`${sizeClasses[size]} ${colorClasses} ${className} select-text whitespace-nowrap relative z-10`}>
            {formattedValue}
        </span>
    );
}
